import * as DateFns from "date-fns";
import _ from "lodash";
import { useCallback } from "react";
import { z } from "zod";
import { BULK_UPLOAD_PEOPLE } from "../components/graphql/mutations";
import { MAX_BIRTH_DATE, MIN_BIRTH_DATE } from "../schemas/Name";
import * as GQL from "../types/graphql";
import { useAvelaToast } from "./useAvelaToast";
import { useOrganization } from "./useOrganization";
import { useRemoteDataMutation } from "./useRemoteDataMutation";

export const Person = z.object({
  id: z.string().nullish(),
  reference_id: z.string().nullish(),
  birth_date: z
    .string()
    .nullish()
    .refine(
      (value) => !value || !DateFns.isBefore(value, MIN_BIRTH_DATE),
      `Date must be on or after ${MIN_BIRTH_DATE}`
    )
    .refine(
      (value) => !value || !DateFns.isAfter(value, MAX_BIRTH_DATE),
      `Date must be on or before ${MAX_BIRTH_DATE}`
    ),
  city: z.string().nullish(),
  first_name: z.string().trim().nullish(),
  last_name: z.string().trim().nullish(),
  middle_name: z.string().trim().nullish(),
  state: z.string().nullish(),
  street_address: z.string().nullish(),
  user_id: z.string().nullish(),
  zip_code: z.string().nullish(),
  email_address: z.string().nullish(),
  phone_number: z.string().nullish(),
});

export type PersonRow = z.infer<typeof Person>;

export function useBulkUploadPeople(
  personType: keyof typeof GQL.person_type_enum
) {
  const organization = useOrganization();
  const [bulkUploadPeople] = useRemoteDataMutation<
    GQL.BulkUploadPeople,
    GQL.BulkUploadPeopleVariables
  >(BULK_UPLOAD_PEOPLE);

  const toast = useAvelaToast({
    id: "bulk-upload-loading",
    title: "Working hard",
    status: "loading",
  });

  return useCallback(
    async function (data: unknown[]) {
      let people: PersonRow[];

      try {
        people = data
          .map((row) => Person.parse(row))
          .filter((item) => Object.keys(item).length > 1); // this filter eliminates an entirely blank row created by the newline at the end of the file
      } catch (e) {
        console.warn("File parse error:", e);
        throw new Error("We were unable to parse this file: " + e);
      }
      if (!people.length) {
        throw new Error("No records found in file.");
      }

      const total = people.length;
      let runningTotal = 0;

      // Split people into chunks of 1000
      const chunkedPeople: PersonRow[][] = _.chunk(people, 1000);

      for (const chunk of chunkedPeople) {
        const variables: GQL.BulkUploadPeopleVariables = {
          people: chunk.map(
            (person): GQL.BulkUploadPeopleVariables["people"][0] => ({
              ...person,
              organization_id: organization.map((d) => d.id).toNullable(),
              person_type: GQL.person_type_enum[personType],
            })
          ),
        };
        await bulkUploadPeople({
          variables,
        });
        runningTotal += chunk.length;
        toast.update("bulk-upload-loading", {
          description: `Importing data...${Math.round(
            (runningTotal / total) * 100
          )}%`,
        });
      }
    },
    [toast, bulkUploadPeople, organization, personType]
  );
}
